import {
  addDays,
  countsForStreak,
  includesMember,
  isTaskDue,
  type TaskInstance,
} from '@shh/shared';
import type { HistoryDay } from '@shh/ui';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { firebase } from '../lib/firebase';
import { isPersonal, type T } from './derive';
import { useFamily } from './family';
import { dateFormat } from '../lib/locale';

const dayFmt = (opts: Intl.DateTimeFormatOptions) => dateFormat({ ...opts, timeZone: 'UTC' });
const short = dayFmt({ weekday: 'short' });
const dm = dayFmt({ day: 'numeric', month: 'numeric' });
const long = dayFmt({ weekday: 'long', day: 'numeric', month: 'long' });
const noon = (day: string) => new Date(`${day}T12:00:00Z`);

/**
 * "Die letzten 7 Tage" for one person: per day, how many of their own tasks were done.
 * Read once (the past does not change while you look); range on `date` only – no extra index.
 * Uses today's task list, so a task created yesterday shows up for earlier days only if due.
 */
export function useHistory(memberId: string, days = 7): HistoryDay[] | undefined {
  const f = useFamily();
  const [instances, setInstances] = useState<TaskInstance[]>();
  const from = addDays(f.today, -days);
  useEffect(() => {
    let alive = true;
    const q = query(
      collection(firebase().db, `households/${f.householdId}/taskInstances`),
      where('date', '>=', from),
      where('date', '<', f.today),
    );
    getDocs(q)
      .then(
        (s) =>
          alive && setInstances(s.docs.map((d) => ({ ...(d.data() as TaskInstance), id: d.id }))),
      )
      .catch(() => alive && setInstances([]));
    return () => {
      alive = false;
    };
  }, [f.householdId, from, f.today]);

  if (!instances) return undefined;
  // own tasks only, like the streaks – shared ones are "whoever is first" and never "missed"
  const mine = f.tasks.filter((t) => isPersonal(t) && includesMember(t.assignees, memberId));
  const doneOn = (t: T, day: string) =>
    instances.some(
      (i) =>
        i.taskId === t.id && i.date === day && i.memberId === memberId && countsForStreak(i.status),
    );
  return Array.from({ length: days }, (_, n) => {
    const day = addDays(from, n);
    const due = mine.filter((t) => t.repeat.kind !== 'flex' && isTaskDue(t, day));
    return {
      key: day,
      label: short.format(noon(day)).replace('.', ''),
      date: dm.format(noon(day)),
      fullLabel: long.format(noon(day)),
      done: due.filter((t) => doneOn(t, day)).length,
      total: due.length,
    };
  });
}
